import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Mic, Square, Trash2, Bot, User } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useVoice } from '../hooks/useVoice';
import styles from './ChatPage.module.css';

const API = process.env.REACT_APP_API_URL || 'https://178.105.190.198.nip.io';
const WS  = API.replace('http', 'ws');

export default function ChatPage() {
  const { token, username } = useAuthStore();
  const [messages, setMessages] = useState([{
    id: 0, role: 'jarvis',
    content: `Buongiorno ${username || 'Matteo'}. Sono JARVIS. Come posso aiutarti oggi?`,
    ts: new Date(),
  }]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [wsReady, setWsReady] = useState(false);
  const ws = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const history = useRef([]);
  const reconnectTimer = useRef(null);
  const retry = useRef(0);
  const shouldReconnect = useRef(true);

  // Invia testo all'agent (usato sia dalla tastiera sia dalla voce).
  const sendText = useCallback((text) => {
    const t = (text || '').trim();
    const s = ws.current;
    if (!t || !s || s.readyState !== WebSocket.OPEN) return;
    const msg = { id: Date.now(), role: 'user', content: t, ts: new Date() };
    setMessages(p => [...p, msg]);
    history.current.push({ role: 'user', content: t });
    s.send(JSON.stringify({
      message: t,
      history: history.current.slice(-20),
      user_name: username || 'Matteo',
    }));
    setThinking(true);
  }, [username]);

  // Voice: motore continuo attivato da pulsante. Quando l'utente ha finito di
  // parlare, onUtterance invia il testo; la risposta viene letta con speak().
  const speakRef = useRef(() => {});
  const sessionOnRef = useRef(false);
  const {
    status: vStatus, sessionOn,
    micToggle, stopSession, interrupt, speak,
  } = useVoice({ onUtterance: sendText });
  useEffect(() => { speakRef.current = speak; }, [speak]);
  useEffect(() => { sessionOnRef.current = sessionOn; }, [sessionOn]);

  // WebSocket con riconnessione automatica.
  // Sopravvive al riavvio dei container backend: quando la connessione cade
  // riprova con backoff esponenziale (+ jitter) finché il backend torna online,
  // così non serve più riaprire l'app manualmente.
  useEffect(() => {
    if (!token) return;
    shouldReconnect.current = true;

    const clearReconnect = () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (!shouldReconnect.current || reconnectTimer.current) return;
      const attempt = retry.current++;
      // 0.5s → 1s → 2s → 4s … cap a 15s, con jitter ±20% per evitare thundering herd.
      const base = Math.min(15000, 500 * 2 ** attempt);
      const delay = base * (0.8 + Math.random() * 0.4);
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        connect();
      }, delay);
    };

    const handleMessage = (e) => {
      const data = JSON.parse(e.data);

      // Cronologia permanente caricata dal DB alla connessione.
      if (data.type === 'history') {
        const loaded = (data.messages || []).map((m, i) => ({
          id: `h${i}`,
          role: m.role === 'assistant' ? 'jarvis' : 'user',
          content: m.content,
          ts: m.ts ? new Date(m.ts) : new Date(),
        }));
        history.current = (data.messages || []).map(m => ({ role: m.role, content: m.content }));
        if (loaded.length) setMessages(prev => [prev[0], ...loaded]);
        return;
      }

      // Richiesta di un tool Mac: eseguilo localmente (solo app desktop) e
      // rimanda il risultato. Non tocca lo stato "thinking": siamo ancora in
      // mezzo all'elaborazione della risposta.
      if (data.type === 'mac_tool') {
        (async () => {
          let content;
          try {
            if (window.electron && window.electron.macTool) {
              content = await window.electron.macTool(data.name, data.input);
            } else {
              content = "Questo strumento è disponibile solo nell'app desktop JARVIS sul Mac.";
            }
          } catch (err) {
            content = '⚠️ Errore esecuzione strumento Mac: ' + (err?.message || err);
          }
          const s = ws.current;
          if (s && s.readyState === WebSocket.OPEN) {
            s.send(JSON.stringify({ type: 'mac_tool_result', id: data.id, content }));
          }
        })();
        return;
      }

      setThinking(false);
      if (data.type === 'message') {
        const msg = { id: Date.now(), role: 'jarvis', content: data.content, ts: new Date() };
        setMessages(p => [...p, msg]);
        history.current.push({ role: 'assistant', content: data.content });

        // In modalità vocale: leggi la risposta ad alta voce (TTS nativo) e
        // poi riparti automaticamente in ascolto per il follow-up.
        if (sessionOnRef.current) {
          try { speakRef.current(data.content); } catch (e) {}
        }

        // Notifica desktop nativa se finestra nascosta
        if (window.electron) {
          window.electron.notify('JARVIS', data.content.slice(0, 100));
        }
      }
      if (data.type === 'error') {
        setMessages(p => [...p, {
          id: Date.now(), role: 'jarvis',
          content: `⚠️ ${data.content}`, ts: new Date(), error: true,
        }]);
      }
    };

    function connect() {
      if (!shouldReconnect.current) return;
      // Il token autentica la connessione; l'user_id reale è derivato dal token lato server.
      const socket = new WebSocket(`${WS}/api/v1/chat/ws/1?token=${encodeURIComponent(token)}`);
      ws.current = socket;

      socket.onopen = () => {
        retry.current = 0;          // reset del backoff appena la connessione è viva
        setWsReady(true);
      };
      socket.onclose = (event) => {
        setWsReady(false);
        // 4401 = token mancante/scaduto (chiuso dal backend). Riconnettere è inutile:
        // chiudo la sessione e l'app rimanda al login per ottenere un token fresco.
        if (event.code === 4401) {
          shouldReconnect.current = false;
          clearReconnect();
          useAuthStore.getState().logout();
          return;
        }
        scheduleReconnect();        // backend caduto/riavviato → riprova
      };
      socket.onerror = () => {
        setWsReady(false);
        socket.close();             // forza onclose, che pianifica la riconnessione
      };
      socket.onmessage = handleMessage;
    }

    connect();

    // Riconnessione immediata quando la rete torna o la finestra riprende il focus:
    // se il backend è già tornato online non si aspetta il prossimo tick di backoff.
    const kick = () => {
      const s = ws.current;
      if (s && (s.readyState === WebSocket.OPEN || s.readyState === WebSocket.CONNECTING)) return;
      retry.current = 0;
      clearReconnect();
      connect();
    };
    window.addEventListener('online', kick);
    window.addEventListener('focus', kick);

    return () => {
      shouldReconnect.current = false;
      clearReconnect();
      window.removeEventListener('online', kick);
      window.removeEventListener('focus', kick);
      if (ws.current) {
        ws.current.onclose = null;  // evita di pianificare una riconnessione durante il cleanup
        ws.current.close();
      }
    };
  }, [token]);

  // Scroll automatico
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    sendText(text);
    setInput('');
    inputRef.current?.focus();
  }, [input, sendText]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const clearChat = () => {
    setMessages([{
      id: 0, role: 'jarvis',
      content: 'Chat azzerata. Come posso aiutarti?',
      ts: new Date(),
    }]);
    history.current = [];
  };

  const voiceLabel = {
    listening: 'In ascolto… parla pure, capisco da solo quando hai finito',
    thinking: 'Trascrivo…',
    speaking: 'JARVIS sta parlando',
  }[vStatus] || '';

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.statusDot} data-ready={wsReady} />
          <span className={styles.headerTitle}>JARVIS Chat</span>
          <span className={styles.model}>
            {wsReady ? 'Connesso' : 'Connessione...'}
          </span>
        </div>
        <button className={styles.clearBtn} onClick={clearChat} title="Azzera chat">
          <Trash2 size={14} />
        </button>
      </div>

      {/* Messages */}
      <div className={styles.messages}>
        {messages.map(m => (
          <div key={m.id} className={`${styles.msg} ${styles[m.role]} fade-in`}>
            <div className={styles.avatar}>
              {m.role === 'jarvis' ? <Bot size={14} /> : <User size={14} />}
            </div>
            <div className={`${styles.bubble} ${m.error ? styles.error : ''}`}>
              <pre className={styles.text}>{m.content}</pre>
              <span className={styles.ts}>
                {m.ts.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>
        ))}

        {thinking && (
          <div className={`${styles.msg} ${styles.jarvis}`}>
            <div className={styles.avatar}><Bot size={14} /></div>
            <div className={styles.bubble}>
              <span className={styles.thinking}>
                <span />
                <span />
                <span />
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Barra stato vocale (visibile solo in modalità voce) */}
      {sessionOn && (
        <div className={styles.voiceStatus} data-state={vStatus}>
          <span className={styles.voiceDot} />
          <span className={styles.voiceLabel}>{voiceLabel}</span>
          {vStatus === 'speaking' && (
            <button className={styles.interruptBtn} onClick={interrupt}>
              Interrompi
            </button>
          )}
          <button className={styles.stopVoiceBtn} onClick={stopSession} title="Esci dalla modalità vocale">
            <Square size={11} /> Stop voce
          </button>
        </div>
      )}

      {/* Input bar */}
      <div className={styles.inputBar}>
        <textarea
          ref={inputRef}
          className={styles.input}
          placeholder={wsReady ? 'Dimmi cosa fare… (Invio per inviare)' : 'Connessione al server…'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          disabled={!wsReady}
        />
        <button
          className={[
            styles.voiceBtn,
            vStatus === 'listening' ? styles.recording : '',
            vStatus === 'speaking' ? styles.speaking : '',
          ].join(' ')}
          onClick={micToggle}
          disabled={!wsReady}
          title={
            !sessionOn ? 'Avvia conversazione vocale'
            : vStatus === 'listening' ? 'Invia adesso (di solito non serve: capisco da solo)'
            : vStatus === 'speaking' ? 'Interrompi'
            : 'Modalità vocale attiva'
          }
        >
          {vStatus === 'listening' ? <Square size={16} /> : <Mic size={16} />}
        </button>
        <button
          className={styles.sendBtn}
          onClick={send}
          disabled={!input.trim() || !wsReady}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
